'use client'

import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import { FormattedPrice } from '@src/blocks/currency'
import FilledStar from '@src/components/product-card/icons/filledStar.inline.svg'
import type { ProductCardTag } from '@src/components/product-card/product-card.types'
import { ProductTags } from '@src/components/product-card/product-image-section/product-tags/product-tags'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Icon } from '@src/quince-components/src'
import { Image } from '@src/quince-components/src/atoms/image/Image'
import Link from '@src/quince-components/src/atoms/link/Link'
import { TAG_LOCATION } from '@src/quince-site-common/src/utils/constants'
import { getFormattedAverageRating } from '@src/quince-site-common/src/utils/getFormattedAverageRating'
import type { SvdNativeCardVariant, SvdNativeProduct } from '@src/types/svd-product-card.types'

import { getInitialSvdVariant, shouldRenderSvdSwatches } from './svd-product-card.helpers'
import styles from './svd-product-card.module.scss'

export interface SvdProductCardProps {
  product: SvdNativeProduct
  className?: string
}

/**
 * First-paint SVD product card (MVP). Renders directly off the native `SvdNativeProduct` — generic
 * anchor swatches, real nullable `displayConfig`, per-variant `spid`/`url`. Core render only:
 * image (+ hover swap to the second image), title, brand, price (+ isRange "from"), rating, swatch
 * row + selection, link. Wishlist / quick-buy / analytics are follow-up PRs. Reached via the
 * section-level `shouldUseSvdCard()` branch; the legacy card is never touched.
 */
export function SvdProductCard({ product, className }: SvdProductCardProps) {
  const { t } = useTranslation('common')
  const { cardVariants, title, thirdPartyDetails, reviews } = product

  const [selectedVariant, setSelectedVariant] = useState<SvdNativeCardVariant | undefined>(() =>
    getInitialSvdVariant(cardVariants),
  )

  useEffect(() => {
    setSelectedVariant(getInitialSvdVariant(cardVariants))
  }, [cardVariants])

  const brandName = thirdPartyDetails.brandDisplayName ?? thirdPartyDetails.brand
  // Parity with the legacy card: hovering swaps the image *source* to the variant's second image
  // (pointer devices only — touch never fires mouseenter). No fade/transition, matching legacy.
  const [isHovered, setIsHovered] = useState(false)
  const hoverImage = selectedVariant?.images[1]
  const displayImage = isHovered && hoverImage ? hoverImage : selectedVariant?.images[0]
  const formattedRating = getFormattedAverageRating(reviews.averageRating)
  const showSwatches = shouldRenderSvdSwatches(product)

  // Backend-driven tags (EDD / white-glove / new / sale, etc.) already arrive on the wire per
  // variant; render them via the shared color-free ProductTags leaf. The dedicated white-glove
  // delivery module (label + EDD) is a later cross-file PR.
  const applicableTags: ProductCardTag[] =
    selectedVariant?.tags.map(tag => ({
      value: tag.value,
      displayName: tag.displayName,
      tagType: tag.tagType as ProductCardTag['tagType'],
    })) ?? []

  return (
    <article className={classnames(styles.svdProductCard, className)} data-testid={TEST_LOCATORS.PRODUCT_CARD}>
      <Link href={selectedVariant?.url ?? product.slug} className={styles.link}>
        <div
          className={styles.imageSection}
          onMouseEnter={() => {
            setIsHovered(true)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
          }}
        >
          <div className={styles.imageContainer}>
            {displayImage && (
              <Image
                url={displayImage.url}
                alt={displayImage.alt || title}
                title={displayImage.alt || title}
                id={displayImage.id}
                height={displayImage.height}
                width={displayImage.width}
                className={styles.image}
              />
            )}
          </div>
          {applicableTags.length > 0 && (
            <ProductTags applicableTags={applicableTags} tagLocation={TAG_LOCATION.PRODUCT_CARD} />
          )}
        </div>
      </Link>

      {showSwatches && (
        <div className={styles.swatchRow} data-testid={TEST_LOCATORS.PRODUCT_CARD_SELECTED_COLOR}>
          {cardVariants.map(variant => {
            const config = variant.displayConfig
            if (!config) return null
            const isSelected = variant.variantId === selectedVariant?.variantId
            return (
              <button
                key={variant.variantId}
                type="button"
                aria-label={config.displayName}
                aria-pressed={isSelected}
                className={classnames(styles.swatch, {
                  [styles.swatchSelected ?? '']: isSelected,
                  [styles.swatchBordered ?? '']: Boolean(config.addBorder),
                })}
                style={config.hexCode ? { backgroundColor: config.hexCode } : undefined}
                onClick={() => {
                  setSelectedVariant(variant)
                }}
              >
                {!config.hexCode && config.imgURL && <img src={config.imgURL} alt={config.displayName} />}
              </button>
            )
          })}
        </div>
      )}

      <Link href={selectedVariant?.url ?? product.slug} className={styles.details}>
        {brandName && <div className={styles.brand}>{brandName}</div>}
        <h2 className={styles.title} data-testid={TEST_LOCATORS.PRODUCT_CARD_TITLE}>
          {title}
        </h2>
        <div className={styles.priceRow} data-testid={TEST_LOCATORS.PRODUCT_CARD_PRICE}>
          {selectedVariant?.price.isRange && <span className={styles.fromPrefix}>{t('from')}</span>}
          <FormattedPrice amount={selectedVariant?.price.salePrice} />
        </div>
        {formattedRating && (
          <div className={styles.rating}>
            <Icon className={styles.ratingIcon} iconSvg={FilledStar} />
            <span>{formattedRating}</span>
            {reviews.reviewCount > 0 && <span className={styles.ratingCount}>{`(${reviews.reviewCount})`}</span>}
          </div>
        )}
      </Link>
    </article>
  )
}
